import { observer } from 'mobx-react-lite'
import { useHistory } from 'react-router-dom'
import Styles from './NotificationItem.module.scss'
import Avatar from 'common/Avatar/Avatar'
import { getInitials } from 'utils/index'
import { Notification } from './types'
import { relativeDateWithTime } from 'utils/dates'
import { NotificationArticle } from './NotificationArticle'
import { NotificationFolder } from './NotificationFolder'
import { MarkAsReadButton } from './MarkAsReadButton'
import { ReactElement } from 'react'
import { useRootStore } from 'stores/RootStore'
import { AriaLabelingProps } from '@react-types/shared'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { Text } from 'blaze-ui/Text/Text'
import { NotificationDesign } from './NotificationDesign'

interface Props extends Pick<AriaLabelingProps, 'aria-label'> {
  notification: Notification
  title: string
  path?: string
  icon: ReactElement
  onClose: () => void
  secondaryAction?: ReactElement
  onClick?: () => void
}

export const BaseNotificationItem = observer(
  ({ notification, onClose, title, path, icon, secondaryAction, onClick, 'aria-label': ariaLabel }: Props) => {
    const { notifications } = useRootStore()
    const history = useHistory()
    const subject = notification.subject
    const userName = subject.sender.display_name
    const currentWorkspace = useCurrentWorkspace()

    const action = () => {
      if (onClick) {
        onClick()
      } else if (path) {
        if (currentWorkspace) notifications.markAsRead(notification.id, currentWorkspace.key)
        history.push(path)
      }

      onClose()
    }

    return (
      <div className={Styles.container} onClick={action} aria-label={ariaLabel || 'Notification Item'}>
        <div className={Styles.user}>
          <Avatar url={subject.sender.avatar_url} label={getInitials(userName)} size={36} />
          <div className={Styles.iconContainer}>{icon}</div>
        </div>
        <div className={Styles.information}>
          <div className={Styles.title}>
            <Text variant="smallList" color="var(--dark-80)">
              {userName}
            </Text>{' '}
            <Text variant="placeholder" color="var(--dark-80)">
              {title}
            </Text>
          </div>

          {'article' in notification && notification.article && <NotificationArticle article={notification.article} />}
          {'folder' in notification && notification.folder && <NotificationFolder folder={notification.folder} />}
          {'design' in notification && notification.design && <NotificationDesign design={notification.design} />}

          <div className={Styles.footer}>
            <Text variant="metadata" color="var(--dark-60)">
              {relativeDateWithTime(notification.updated_at)}
            </Text>
            {secondaryAction}
          </div>
        </div>

        {!notification.read && (
          <div className={Styles.readButton}>
            <MarkAsReadButton notificationId={notification.id} />
          </div>
        )}
        {!notification.seen && !notification.read && <div className={Styles.unseenDot} />}
      </div>
    )
  },
)
